import { apiCall } from "./AxiosService";
import { API_URL } from "./APIRoute";

const BASE = API_URL.BASEURL;

/**
 * Database Service
 * Handles all database-related API calls
 * API Routes: /api/v1/database/*
 */
export const DatabaseService = {
  /**
   * Get all databases for the current user
   */
  getAllDatabases: async () => {
    try {
      const response = await apiCall({
        method: "GET",
        url: `${BASE}database`,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get database by ID
   * @param {string} id - Database ID
   */
  getDatabaseById: async (id) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: `${BASE}database/${id}`,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get database status from container monitoring API
   * @param {string} id - Database ID
   */
  getDatabaseStatus: async (id) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: `${BASE}database/${id}/status`,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get detailed deployment stage tracking with K8s resource status
   * @param {string} id - Database ID
   * Returns: currentStage, currentStageIndex, progressPercent, isComplete, isFailed, 
   *          resource status (namespace, secret, pvc, deployment, service), pod status, network status
   */
  getDeploymentStage: async (id) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: `${BASE}database/${id}/deployment-stage`,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get database metrics from container monitoring API
   * @param {string} id - Database ID
   */
  getDatabaseMetrics: async (id) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: `${BASE}database/${id}/metrics`,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get database connections from container monitoring API
   * @param {string} id - Database ID
   */
  getDatabaseConnections: async (id) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: `${BASE}database/${id}/connections`,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get ALL monitoring data in a single batched call
   * @param {string} id - Database ID
   */
  getMonitoringAll: async (id) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: `${BASE}database/${id}/monitoring/all`,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Create new database
   * @param {Object} data - Database configuration
   */
  createDatabase: async (data) => {
    try {
      const response = await apiCall({
        method: "POST",
        url: `${BASE}database`,
        body: data,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update database
   * @param {string} id - Database ID
   * @param {Object} data - Update data
   */
  updateDatabase: async (id, data) => {
    try {
      const response = await apiCall({
        method: "PUT",
        url: `${BASE}database/${id}`,
        body: data,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete database
   * @param {string} id - Database ID
   * @param {string} [password] - Required for production databases
   */
  deleteDatabase: async (id, password) => {
    try {
      const url = password
        ? `${BASE}database/${id}?password=${encodeURIComponent(password)}`
        : `${BASE}database/${id}`;
      const response = await apiCall({
        method: "DELETE",
        url,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Start database
   * @param {string} id - Database ID
   */
  startDatabase: async (id) => {
    try {
      const response = await apiCall({
        method: "POST",
        url: `${BASE}database/${id}/start`,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Stop database
   * @param {string} id - Database ID
   */
  stopDatabase: async (id) => {
    try {
      const response = await apiCall({
        method: "POST",
        url: `${BASE}database/${id}/stop`,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Restart database
   * @param {string} id - Database ID
   */
  restartDatabase: async (id) => {
    try {
      const response = await apiCall({
        method: "POST",
        url: `${BASE}database/${id}/restart`,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get available database types
   */
  getDatabaseTypes: async () => {
    try {
      const response = await apiCall({
        method: "GET",
        url: `${BASE}database/types`,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get database versions by type
   * @param {string} type - Database type (postgresql, mysql, etc.)
   */
  getDatabaseVersions: async (type) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: `${BASE}database/types/${type}/versions`,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get database tiers by type
   * @param {string} type - Database type (postgresql, mysql, etc.)
   */
  getDatabaseTiers: async (type) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: `${BASE}database/types/${type}/tiers`,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get database statistics (counts by status)
   */
  getStats: async () => {
    try {
      const response = await apiCall({
        method: "GET",
        url: `${BASE}database/stats`,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get connection details for a database
   * @param {string} id - Database ID
   */
  getConnection: async (id) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: `${BASE}database/${id}/connection`,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Reset database password
   * @param {string} id - Database ID
   */
  resetPassword: async (id) => {
    try {
      const response = await apiCall({
        method: "POST",
        url: `${BASE}database/${id}/reset-password`,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get metrics history
   * @param {string} id - Database ID
   * @param {number} hours - Number of hours to fetch (default: 24)
   */
  getMetricsHistory: async (id, hours = 24) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: `${BASE}database/${id}/metrics/history?hours=${hours}`,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
};

export default DatabaseService;
